import { caseApiRequest, dataPayloadErrorMessage, isDataPayloadSuccess } from './apiClient';
import type { ApiResult } from './types';

export type IntranetForwardUserRow = {
  case_request_category_allocation_user_id?: string;
  caseRequestCategoryAllocationUserId?: string;
  user_id?: string;
  userId?: string;
  user_display_name?: string;
  userDisplayName?: string;
  user_title?: string;
  userTitle?: string;
};

export type DocumentMovementOfficerRow = {
  user_id?: string;
  userId?: string;
  display_name?: string;
  displayName?: string;
  username?: string;
  verification_email?: string;
  verificationEmail?: string;
  current_role?: string;
  currentRole?: string;
};

function strField(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeForwardUsers(raw: unknown): IntranetForwardUserRow[] {
  if (!Array.isArray(raw)) return [];
  const out: IntranetForwardUserRow[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    out.push(item as IntranetForwardUserRow);
  }
  return out;
}

function normalizeDocumentMovers(raw: unknown): DocumentMovementOfficerRow[] {
  if (!Array.isArray(raw)) return [];
  const out: DocumentMovementOfficerRow[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    out.push(item as DocumentMovementOfficerRow);
  }
  return out;
}

export async function listEligibleForwardUsers(input: {
  categoryId: string;
  subCategoryId: string;
  signal?: AbortSignal;
}): Promise<ApiResult<IntranetForwardUserRow[]>> {
  const categoryId = input.categoryId.trim();
  const subCategoryId = input.subCategoryId.trim();
  if (!categoryId || !subCategoryId) {
    return { success: true, data: [] };
  }

  const res = await caseApiRequest<unknown>('/users/intranet', {
    method: 'POST',
    body: { category_id: categoryId, sub_category_id: subCategoryId },
    signal: input.signal,
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeForwardUsers(res.data) };
}

/**
 * CMS-CCA-9
 * POST /v1/case-requests/workflow-tasks/review-original-owner
 *
 * If the input task is REVIEW, returns the original forward owner (CMS-CCA-2 response shape).
 */
export async function getReviewOriginalOwner(input: {
  taskId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<IntranetForwardUserRow[]>> {
  const taskId = input.taskId.trim();
  if (!taskId) return { success: false, message: 'Invalid task id.' };

  const applicationCode = (input.applicationCode ?? 'INTRANET').trim();
  const res = await caseApiRequest<unknown>('/workflow-tasks/review-original-owner', {
    method: 'POST',
    body: { task_id: taskId },
    signal: input.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeForwardUsers(res.data) };
}

export async function listDocumentMovementOfficers(signal?: AbortSignal): Promise<ApiResult<DocumentMovementOfficerRow[]>> {
  const res = await caseApiRequest<unknown>('/users/document-movement-officers', { signal });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeDocumentMovers(res.data) };
}

export async function forwardUserTask(input: {
  workflowInstanceStageUserTaskId: string;
  forwardUserId: string;
  physicalFileMovementRequired: boolean;
  documentMoverId?: string;
}): Promise<ApiResult<void>> {
  const workflowInstanceStageUserTaskId = input.workflowInstanceStageUserTaskId.trim();
  const forwardUserId = input.forwardUserId.trim();
  const documentMoverId = (input.documentMoverId ?? '').trim();

  if (!workflowInstanceStageUserTaskId) return { success: false, message: 'User task id is missing.' };
  if (!forwardUserId) return { success: false, message: 'Forward user is required.' };
  if (input.physicalFileMovementRequired && !documentMoverId) {
    return { success: false, message: 'Document mover is required.' };
  }

  const res = await caseApiRequest<{ status?: string; message?: string } | unknown>('/forward-user-task', {
    method: 'POST',
    body: {
      forwardUserId,
      documentMoverId: input.physicalFileMovementRequired ? documentMoverId : null,
      workflowInstanceStageUserTaskId,
      physicalFileMovementRequired: input.physicalFileMovementRequired,
    },
  });

  if (!res.ok) return { success: false, message: res.message };
  if (!isDataPayloadSuccess(res.data)) {
    return { success: false, message: dataPayloadErrorMessage(res.data) };
  }
  return { success: true, data: undefined };
}

export function forwardUserLabel(row: IntranetForwardUserRow): { id: string; label: string } | null {
  const r = row as unknown as Record<string, unknown>;
  const userId = strField(r, 'user_id', 'userId');
  if (!userId) return null;
  const name = strField(r, 'user_display_name', 'userDisplayName');
  const label = name || userId;
  return { id: userId, label };
}

export function documentMoverLabel(row: DocumentMovementOfficerRow): { id: string; label: string } | null {
  const r = row as unknown as Record<string, unknown>;
  const userId = strField(r, 'user_id', 'userId');
  if (!userId) return null;
  const name = strField(r, 'display_name', 'displayName');
  const label = name || userId;
  return { id: userId, label };
}

